import classNames from "classnames";
import {
  InputNumber,
  InputNumberChangeEvent,
  InputNumberProps,
  InputNumberValueChangeEvent
} from "primereact/inputnumber";
import React, { useMemo } from "react";

import { toWSMoneyString } from "../../../utils";
import { getDataTestId } from "../../../utils/getDataTestId";
import { getFsExcludeClassName } from "../../../utils/getFsExcludeClassName";
import { WSInputBaseProps } from "../../types/forms";
import {
  detachFieldProps,
  getFieldAriaProps,
  WSField,
  WSFieldProps
} from "../WSField/WSField";
import {
  WSInputContainer,
  WSInputContainerProps
} from "../WSInputContainer/WSInputContainer";

import styles from "./styles.module.scss";

export type WSInputNumberProps = WSInputBaseProps<
  number | null,
  InputNumberChangeEvent | InputNumberValueChangeEvent
> &
  WSFieldProps &
  WSInputContainerProps &
  Omit<
    InputNumberProps,
    | "pt"
    | "prefix"
    | "size"
    | "color"
    | "onChange"
    | "onBlur"
    | "onFocus"
    | "mode"
  >;

export const WSInputNumber = React.forwardRef<InputNumber, WSInputNumberProps>(
  (props, ref) => {
    const {
      name,
      status,
      align,
      size = "M",
      className,
      icon,
      placeholder = "0",
      iconRight,
      inputAction,
      disabled,
      onChange,
      fsExclude,
      inputClassName,
      ...otherProps
    } = props;

    const { fieldProps, currency, ...inputProps } = detachFieldProps(
      otherProps
    );

    const currencySymbol = useMemo(() => {
      if (!currency) {
        return;
      }

      try {
        return toWSMoneyString(0, "default", currency).replace("0.00", "");
      } catch {
        return;
      }
    }, [currency]);

    const testIdBase = getDataTestId("inputNumber", name, fieldProps.label);
    const ariaProps = getFieldAriaProps({
      name,
      label: fieldProps.label,
      helperText: fieldProps.helperText,
      required: fieldProps.required,
      status,
      message: fieldProps.message
    });

    return (
      <WSField
        {...fieldProps}
        name={name}
        status={status}
        data-testid={testIdBase}
      >
        <WSInputContainer
          status={status}
          align={align}
          size={size}
          icon={icon}
          iconRight={iconRight}
          inputAction={inputAction}
          disabled={disabled}
          className={className}
          prefix={currencySymbol}
        >
          <InputNumber
            ref={ref}
            name={name}
            disabled={disabled}
            onChange={(e) => onChange(e.value, e)}
            // Trigger change event when validations are completed
            onValueChange={(e) => onChange(e.value || null, e)}
            className={classNames(
              inputClassName,
              getFsExcludeClassName(fsExclude),
              styles.span
            )}
            placeholder={placeholder}
            pt={{ input: { root: { "data-testid": `${testIdBase}Input` } } }}
            {...ariaProps}
            {...inputProps}
          />
        </WSInputContainer>
      </WSField>
    );
  }
);

WSInputNumber.displayName = "WSInputNumber";
